import { StanBadge, StanTable } from '@/components';
import { formatTimeTo12Hour, parseLocalDate } from '@/lib/utils/timeUtils';
import type { Appointment } from '../types/appointment.types';
import type { StanColumnDef } from '@/components/core-components/StanTable/StanTable.types';

interface AppointmentsListDesktopProps {
    appointments: Appointment[];
    isLoading?: boolean;
    onAppointmentClick?: (appointment: Appointment) => void;
    page?: number;
    totalPages?: number;
    onPageChange?: (page: number) => void;
}

export function AppointmentsListDesktop({
    appointments,
    isLoading = false,
    onAppointmentClick,
    page = 1,
    totalPages,
    onPageChange,
}: AppointmentsListDesktopProps) {
    const columns: StanColumnDef<Appointment>[] = [
        {
            accessorKey: 'title',
            header: 'Title',
            cell: ({ getValue }) => {
                const title = getValue() as string;
                const truncatedTitle = title.length > 25 ? `${title.substring(0, 25)}...` : title;

                return <span className='text-dark font-medium'>{truncatedTitle}</span>;
            },
        },
        {
            accessorKey: 'attendees',
            header: 'Attendees',
            width: '300px',
            cell: ({ getValue, row }) => {
                const attendees = getValue() as string | number;
                const isCanceled = row.original.status === 'canceled';
                const attendeesList = row.original.attendeesList;

                let displayText = '';
                if (typeof attendees === 'number') {
                    if (attendees === 1 && attendeesList && attendeesList.length === 1 && attendeesList[0]) {
                        displayText = attendeesList[0].email;
                    } else {
                        displayText = `${attendees} Attendees`;
                    }
                } else {
                    displayText = attendees;
                }

                return (
                    <div className='flex items-center gap-2'>
                        <span className='text-gray truncate'>{displayText}</span>
                        {isCanceled && (
                            <StanBadge variant='destructive' size='sm' className='flex-shrink-0'>
                                Canceled
                            </StanBadge>
                        )}
                    </div>
                );
            },
        },
        {
            accessorKey: 'date',
            header: 'Date',
            cell: ({ getValue }) => {
                const date = parseLocalDate(getValue() as string);
                return (
                    <span className='text-gray'>
                        {date.toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric',
                        })}
                    </span>
                );
            },
        },
        {
            accessorKey: 'slot',
            header: 'Slot',
            cell: ({ row }) => {
                const { startTime, endTime } = row.original;
                return (
                    <span className='text-gray'>
                        {formatTimeTo12Hour(startTime)} - {formatTimeTo12Hour(endTime)}
                    </span>
                );
            },
        },
    ];

    return (
        <StanTable
            columns={columns}
            data={appointments}
            isLoading={isLoading}
            onRowClick={onAppointmentClick}
            page={page}
            totalPages={totalPages}
            onPageChange={onPageChange}
            emptyHeader='No Appointments'
            emptySubheader="You don't have any appointments scheduled yet."
        />
    );
}
